import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Button, Alert, CircularProgress, TextField, Paper, Grid, Chip } from '@mui/material';
import api from '../utils/api';

const MartEntryScanner = () => {
  const [inputValue, setInputValue] = useState('');
  const [scannedData, setScannedData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  const handleScan = async () => {
    if (inputValue) {
      setLoading(true);
      setError(null);

      try {
        const response = await api.post('buyers/mart-entry/', { qr_code: inputValue });
        setScannedData(response.data);
        setInputValue(''); // Clear input after successful scan
      } catch (err) {
        setError(err.response?.data?.error || 'An error occurred while processing the input');
      } finally {
        setLoading(false);
        inputRef.current.focus(); // Refocus input after scan
      }
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Mart Entry Scanner
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 2 }}>
            <TextField
              label="Enter QR Code"
              variant="outlined"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleScan();
                }
              }}
              inputRef={inputRef}
              fullWidth
              autoFocus
            />
          </Box>
          <Button variant="contained" onClick={handleScan} disabled={!inputValue || loading}>
            Process QR Code
          </Button>
          {loading && <CircularProgress sx={{ ml: 2 }} />}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Grid>
        <Grid item xs={12} md={6}>
          {scannedData && (
            <Paper elevation={3} sx={{ p: 2, bgcolor: scannedData.created ? 'success.light' : 'info.light', height: '100%' }}>
              {scannedData.entry_count > 1 && (
                <Alert severity="error" variant="filled" sx={{ mb: 2, fontSize: '1.2rem', fontWeight: 'bold' }}>
                  Warning: This person is already inside!
                </Alert>
              )}
              <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                {scannedData.created ? 'New Entry' : 'Returning Entry'}
              </Typography>
              <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Typography variant="h6" gutterBottom>Scanned Pass Information:</Typography>
                    <Typography><strong>Name:</strong> {scannedData.name}</Typography>
                    <Chip 
                      label={scannedData.pass_type} 
                      color="primary" 
                      sx={{ mb: 1 }}
                    />
                    <Typography sx={{ 
                      bgcolor: 'primary.light', 
                      color: 'primary.contrastText', 
                      p: 1, 
                      borderRadius: 1,
                      display: 'inline-block',
                      mt: 1,
                      mb: 1
                    }}>
                      <strong>Entry Count:</strong> {scannedData.entry_count}
                    </Typography>
                    
                    {/* Count Statistics */}
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Typography variant="h6" gutterBottom>Today's Statistics:</Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Total Entries: {scannedData.total_entries || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Buyers: {scannedData.buyers_count || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Sellers: {scannedData.sellers_count || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Others: {scannedData.other_count || 0}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {scannedData.photo && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <img 
                          src={scannedData.photo} 
                          alt="Pass Holder Photo" 
                          style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }} 
                        />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default MartEntryScanner;
